import { cn } from '@atlas/ui-primitives'
import { ChevronUp } from 'lucide-react'
import { type ComponentPropsWithRef, type ReactNode, useId, useState } from 'react'
import { CardShell } from '../card-shell/card-shell'
import { SampleDataNotice } from '../sample-data-notice/sample-data-notice'

/**
 * The card shell every content section shares: a heading row (title, optional count,
 * optional right-hand action) over the body.
 *
 * `samplePending` is the whole disclosure mechanism - passing it renders the sample
 * notice in the heading, so a card cannot show sample data without saying so.
 */
export interface SectionCardProps extends Omit<ComponentPropsWithRef<'section'>, 'title'> {
	title: ReactNode
	/** Glyph before the title. Decorative - the heading already names the section. */
	icon?: ReactNode
	/**
	 * Rendered as `· N` beside the title. Omit it while a read is in flight or after one
	 * failed: a count is an answer, and there is none yet.
	 */
	count?: number
	/** Right-aligned link/affordance, e.g. an `All rules` link. */
	action?: ReactNode
	/** Name the model that would replace this card's sample data; omit for real-data cards. */
	samplePending?: string
	/** Muted supporting line under the heading row. */
	description?: ReactNode
	/** Makes the title a toggle that hides the description and body; the card starts open. */
	collapsible?: boolean
	children: ReactNode
	className?: string
}

export function SectionCard({
	title,
	icon,
	count,
	action,
	samplePending,
	description,
	collapsible = false,
	children,
	className,
	...rest
}: SectionCardProps) {
	const [open, setOpen] = useState(true)
	const bodyId = useId()
	return (
		<CardShell as="section" {...rest} className={className}>
			<div className={cn(open && 'mb-4')}>
				<div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
					<div className={cn('flex min-w-0 items-center gap-2', collapsible && 'flex-1')}>
						{icon}
						<h2
							className={cn(
								'font-ds-display text-ds-base leading-6 font-semibold text-ds-text',
								collapsible && 'flex-1',
							)}
						>
							{collapsible ? (
								<button
									type="button"
									onClick={() => setOpen((v) => !v)}
									aria-expanded={open}
									aria-controls={bodyId}
									className="flex w-full items-center justify-between gap-2 text-left"
								>
									{title}
									<ChevronUp
										aria-hidden
										className={cn('h-4 w-4 shrink-0 text-ds-primary transition-transform', !open && 'rotate-180')}
									/>
								</button>
							) : (
								title
							)}
						</h2>
						{count !== undefined ? (
							<span className="font-mono text-ds-xs leading-4 text-ds-text-subtle">· {count}</span>
						) : null}
						{samplePending ? <SampleDataNotice pending={samplePending} /> : null}
					</div>
					{action ? <div className="shrink-0 text-ds-sm leading-5">{action}</div> : null}
				</div>
				{open && description ? <p className="mt-1 text-ds-sm text-ds-text-muted">{description}</p> : null}
			</div>
			{collapsible ? open && <div id={bodyId}>{children}</div> : children}
		</CardShell>
	)
}
